import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Send, Bot, User, Copy, Share2, X, Minimize2, Maximize2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Message {
  id: number;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const FloatingAIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Halo! Saya AI assistant matematika. Apa yang ingin Anda tanyakan tentang perkalian?",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const sendMessageToAI = async (message: string) => {
    try {
      const response = await fetch(`https://text.pollinations.ai/${encodeURIComponent(message)}`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error('Failed to get response from AI');
      }
      
      const aiResponse = await response.text();
      return aiResponse;
    } catch (error) {
      console.error('Error calling AI API:', error);
      return "Maaf, saya mengalami kesulitan untuk merespons saat ini. Silakan coba lagi nanti.";
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputMessage,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const contextualPrompt = `Sebagai guru matematika yang ahli dalam perkalian, tolong jawab pertanyaan berikut dengan penjelasan yang mudah dipahami dan berikan contoh jika diperlukan. Pertanyaan: ${inputMessage}`;
      
      const aiResponse = await sendMessageToAI(contextualPrompt);
      
      const aiMessage: Message = {
        id: messages.length + 2,
        text: aiResponse,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal mendapatkan respons dari AI. Silakan coba lagi.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleCopyMessage = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Berhasil!",
        description: "Pesan telah disalin ke clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal menyalin pesan",
        variant: "destructive",
      });
    }
  };

  const handleShareMessage = async (text: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Jawaban AI Matematika",
          text: text,
        });
      } catch (error) {
        console.log("Error sharing:", error);
      }
    } else {
      handleCopyMessage(text);
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          size="lg"
          className="rounded-full w-16 h-16 shadow-2xl bg-gradient-to-r from-primary to-secondary hover:scale-110 transition-all duration-300 glow-effect"
        >
          <MessageCircle className="w-8 h-8" />
        </Button>
        <div className="absolute -top-12 right-0 bg-primary text-primary-foreground px-3 py-1 rounded-lg text-sm whitespace-nowrap animate-bounce">
          Tanya AI! 🤖
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Card className={`w-96 shadow-2xl ${isMinimized ? 'h-16' : 'h-[500px]'} transition-all duration-300 futuristic-card bg-gradient-to-br from-background/95 to-card/95 backdrop-blur-sm overflow-hidden`}>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">AI Assistant</CardTitle>
          </div>
          <div className="flex gap-1">
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        {!isMinimized && (
          <CardContent className="p-4 pt-0">
            <div className="space-y-4">
              <Card className="h-64">
                <ScrollArea className="h-full p-3">
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-lg p-2 text-sm ${
                            message.isUser
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {!message.isUser && <Bot className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                            <div className="flex-1">
                              <div className="text-xs">{message.text}</div>
                              <div className="flex items-center justify-between mt-1">
                                <div className="text-xs opacity-70">
                                  {formatTime(message.timestamp)}
                                </div>
                                {!message.isUser && (
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleCopyMessage(message.text)}
                                      className="h-5 px-1 text-xs"
                                    >
                                      <Copy className="w-2.5 h-2.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleShareMessage(message.text)}
                                      className="h-5 px-1 text-xs"
                                    >
                                      <Share2 className="w-2.5 h-2.5" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                            {message.isUser && <User className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="max-w-[85%] rounded-lg p-2 bg-muted">
                          <div className="flex items-center gap-2">
                            <Bot className="w-3 h-3" />
                            <div className="flex space-x-1">
                              <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"></div>
                              <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </Card>

              <div className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Tanya tentang matematika..."
                  disabled={isLoading}
                  className="flex-1 text-sm"
                />
                <Button 
                  onClick={handleSendMessage} 
                  disabled={!inputMessage.trim() || isLoading}
                  size="sm"
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default FloatingAIChat;